import {
  AuditEventSubtype,
  ExtendedAuditEvent,
} from '@seen/core/models/audit/ExtendedAuditEvent'
import type { Participant } from '@seen/core/models/patients/participant'
import { Button, HoverCard, HoverCardContent, HoverCardTrigger } from '@seen/ui'
import { IconAlertCircle, IconCheck, IconRefresh } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { format, formatDistanceToNow } from 'date-fns'
import type { JSX } from 'react'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface CairSyncStatusProps {
  participant: Participant
}

export function CairSyncStatus({
  participant,
}: CairSyncStatusProps): JSX.Element {
  const { seenApi } = useSeenApi()
  const isReadOnly = useIsReadOnly()

  const { data, refetch } = useSearch(
    ExtendedAuditEvent,
    ExtendedAuditEvent.byTypeAndPatient(
      AuditEventSubtype.CAIR_SYNC,
      participant.reference
    )
  )

  const mutation = useMutation({
    mutationFn: async () => {
      await seenApi.post('/cair-sync', { patientId: participant.id })
    },
    onSuccess: () => {
      void refetch()
    },
  })

  const lastSync = data?.[0]
  const syncHistory = data ?? []

  const timeAgo = lastSync?.recorded
    ? formatDistanceToNow(new Date(lastSync.recorded), { addSuffix: true })
    : 'Unknown'

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger>
        <span className="text-xs text-muted-foreground">
          Last CAIR sync: {timeAgo}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-96" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">CAIR Sync History</h4>
            {!isReadOnly && (
              <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
                variant="outline"
                size="sm"
                className="gap-1.5"
              >
                <IconRefresh
                  className={`size-3.5 ${mutation.isPending ? 'animate-spin' : ''}`}
                />
                {mutation.isPending ? 'Syncing...' : 'Sync Now'}
              </Button>
            )}
          </div>

          {syncHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No sync history available
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {syncHistory.map((event) => {
                const isSuccess = event.isSuccess
                const timestamp = event.recorded
                  ? new Date(event.recorded)
                  : null

                return (
                  <div
                    key={event.id}
                    className="flex items-start gap-2 border-b pb-2 text-sm last:border-b-0 last:pb-0"
                  >
                    <div className="mt-0.5">
                      {isSuccess ? (
                        <IconCheck className="size-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <IconAlertCircle className="size-4 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            isSuccess ? 'text-foreground' : 'text-red-600'
                          }
                        >
                          {isSuccess ? 'Success' : 'Failed'}
                        </span>
                        {timestamp && (
                          <span className="text-xs text-muted-foreground">
                            {format(timestamp, 'MMM d, yyyy h:mm a')}
                          </span>
                        )}
                      </div>
                      {event.outcomeDesc && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {event.outcomeDesc}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
